import React, { useState, useContext } from 'react';
import OnboardingSaveSteps from 'context/onboarding-save-steps/context';

import {
  permanentResidencyValidation,
  generalDataRemapper,
  economicInfoDataRemapper,
  bankAccountRemapper,
} from 'context/onboarding-save-steps/helpers';

import {
  createCoOwner,
  updateCoOwner,
  updateCoOwnerInvestmentAccount,
  updatePhysicalEconomicInformation,
  createPhysicalBankAccount,
  updatePhysicalBankAccount,
} from 'api/client';
import type { CoOwner } from 'api/client/models';

import type {
  BankAccountValues,
  EconomicInformationValues,
  GeneralDataValues,
} from 'context/onboarding-save-steps/models';

import { StepFieldsConfig } from 'components/NewClient/Onboarding/config';

import type { InvestmentAccountValues } from './models/investment-account';

import CoOwnersManagement from './context';
import type { CoOwnersManagementType } from './types';

const initialState = { saved: false };
const initialStepGeneralState = {
  clientId: null,
  coOwnerId: null,
  contractId: null,
  name: '',
  fathersLastName: '',
  mothersLastName: '',
  fullName: '',
  saved: false,
};

interface Props {
  children: React.ReactNode;
}

const CoOwnersManagementProvider = ({ children }: Props) => {
  const { dataStepGeneral, dataStepCoOwner, setDataStepCoOwner } =
    useContext(OnboardingSaveSteps);

  const [coOwnerData, setCoOwnerData] = useState<CoOwner | null>(null);

  const [dataCoOwnerStepGeneral, setDataCoOwnerStepGeneral] =
    useState<GeneralDataValues>({
      ...initialStepGeneralState,
    } as GeneralDataValues);

  const [dataCoOwnerStepEconomicInfo, setDataCoOwnerStepEconomicInfo] =
    useState<EconomicInformationValues>(initialState);

  const [dataCoOwnerStepAccount, setDataCoOwnerStepAccount] =
    useState<BankAccountValues>(initialState);

  const [
    dataCoOwnerStepInvestmentAccount,
    setDataCoOwnerStepInvestmentAccount,
  ] = useState<InvestmentAccountValues>(initialState);

  const resetCoOwnersPersonSteps = () => {
    setCoOwnerData(null);
    setDataCoOwnerStepGeneral(initialState as GeneralDataValues);
    setDataCoOwnerStepEconomicInfo(initialState);
    setDataCoOwnerStepAccount(initialState);
    setDataCoOwnerStepInvestmentAccount(initialState);
  };

  const investmentAccountsRemapper = (values: InvestmentAccountValues) => {
    return {
      clientId: coOwnerData?.clientId,
      contractId: coOwnerData?.contractId,
      kycId: coOwnerData?.coOwnerId,
      cveRole: 'CO',
      investmentAccounts: values.investmentAccounts?.map((item: any) => ({
        investmentAccount: item.value,
        brokerageHouse: item.brokerageHouse,
      })),
    };
  };

  const saveCoOwnerStepGeneralData = async (
    values: GeneralDataValues,
    clientRole: string,
  ) => {
    const permanentResidency = permanentResidencyValidation(values);
    const finalValues = generalDataRemapper(
      values,
      permanentResidency,
      clientRole,
      dataStepGeneral,
    );

    finalValues.cveParentesco =
      values.cveParentesco && values.cveParentesco.value;

    const { data, success } = dataCoOwnerStepGeneral.saved
      ? await updateCoOwner(finalValues)
      : await createCoOwner(finalValues);

    if (success && data) {
      const generalData = {
        ...finalValues,
        clientId: data.clientId,
        coOwner: data.contractId,
        contractId: data.contractId,
        coOwnerId: data.generalData?.coOwnerId || 0,
        birthDate: data.generalData?.birthDate || finalValues.birthDate,
        saved: true,
      };

      setDataCoOwnerStepGeneral(generalData);

      let { coOwners = [] } = dataStepCoOwner || {};

      if (finalValues.coOwnerId) {
        coOwners = coOwners.map((item: any) => {
          if (item.coOwnerId === finalValues.coOwnerId) {
            return {
              ...item,
              generalData,
            };
          }
          return item;
        });
      } else {
        const coOwnerId = data.generalData?.coOwnerId || 0;
        const basicData = {
          clientId: data.clientId,
          coOwner: data.contractId,
          contractId: data.contractId,
          coOwnerId,
          kycId: coOwnerId,
        };

        const newCoOwner = {
          ...basicData,
          bankAccounts: [],
          generalData,
          economicInformation: {
            initialState,
          },
          investmentAccounts: {
            ...basicData,
            investmentAccounts: null,
          },
        };

        setCoOwnerData(newCoOwner as any);
        coOwners.push(newCoOwner as any);
      }

      setDataStepCoOwner({ ...dataStepCoOwner, coOwners: [...coOwners] });

      return { success };
    }
    return { success: false };
  };

  const saveCoOwneStepEconomicInfo = async (
    values: EconomicInformationValues,
    clientRole: string,
    fieldsConfig: StepFieldsConfig<'fisica', 'economicInfo'>,
  ) => {
    const finalValues = economicInfoDataRemapper(
      values,
      dataStepGeneral,
      clientRole,
      fieldsConfig,
    );

    const saveStepResponse =
      await updatePhysicalEconomicInformation(finalValues);

    if (saveStepResponse.success) {
      setDataCoOwnerStepEconomicInfo({
        ...finalValues,
        ...saveStepResponse,
        saved: true,
      } as unknown as EconomicInformationValues);

      let { coOwners = [] } = dataStepCoOwner || {};

      coOwners = coOwners.map((item: any) => {
        if (item.coOwnerId === finalValues.coOwnerId) {
          return {
            ...item,
            economicInformation: {
              ...finalValues,
              ...saveStepResponse,
              saved: true,
            },
          };
        }
        return item;
      });

      setDataStepCoOwner({ ...dataStepCoOwner, coOwners: [...coOwners] });
      return { success: true };
    }
    return { success: false };
  };

  const saveCoOwnerStepBankAccount = async (
    values: BankAccountValues,
    clientRole: string,
  ) => {
    const finalValues = bankAccountRemapper(
      values,
      dataStepGeneral,
      clientRole,
    );

    const saveStepResponse = dataCoOwnerStepAccount.saved
      ? await updatePhysicalBankAccount(finalValues)
      : await createPhysicalBankAccount(finalValues);

    if (saveStepResponse.success) {
      setDataCoOwnerStepAccount({
        ...finalValues,
        ...saveStepResponse,
        saved: true,
      });
      let { coOwners = [] } = dataStepCoOwner || {};
      coOwners = coOwners.map((item: any) => {
        if (item.coOwnerId === finalValues.coOwnerId) {
          return {
            ...item,
            bankAccounts: saveStepResponse?.data?.bankAccounts || [{}],
          };
        }
        return item;
      });

      setDataStepCoOwner({ ...dataStepCoOwner, coOwners: [...coOwners] });
      return { success: true };
    }
    return { success: false, error: saveStepResponse };
  };

  const saveCoOwnerInvestmentAccounts = async (
    values: InvestmentAccountValues,
  ) => {
    const finalValues = investmentAccountsRemapper(values);
    const { success, errorMessage } =
      await updateCoOwnerInvestmentAccount(finalValues);

    if (success) {
      let { coOwners = [] } = dataStepCoOwner || {};
      coOwners = coOwners.map((item: any) => {
        if (item.coOwnerId === values.coOwnerId) {
          return {
            ...item,
            investmentAccounts: finalValues,
          };
        }
        return item;
      });

      setDataCoOwnerStepInvestmentAccount({
        ...finalValues,
        saved: true,
      } as InvestmentAccountValues);

      setDataStepCoOwner({ ...dataStepCoOwner, coOwners: [...coOwners] });
      return { success: true };
    }
    return { success, errorMessage };
  };

  const value: CoOwnersManagementType = {
    coOwnerData,
    dataCoOwnerStepGeneral,
    dataCoOwnerStepEconomicInfo,
    dataCoOwnerStepAccount,
    dataCoOwnerStepInvestmentAccount,
    setCoOwnerData,
    setDataCoOwnerStepGeneral,
    setDataCoOwnerStepEconomicInfo,
    setDataCoOwnerStepAccount,
    setDataCoOwnerStepInvestmentAccount,
    saveCoOwnerStepGeneralData,
    saveCoOwneStepEconomicInfo,
    saveCoOwnerStepBankAccount,
    saveCoOwnerInvestmentAccounts,
    resetCoOwnersPersonSteps,
  };
  return (
    <CoOwnersManagement.Provider value={value}>
      {children}
    </CoOwnersManagement.Provider>
  );
};

export default CoOwnersManagementProvider;
